<!--
  Google Maps component for displaying property markers
  
  2025-06-08: Added city zoom functionality using Geocoding API
  2025-05-27: Fixed clustering by using GridAlgorithm with maxZoom: 10 for proper zoom-based clustering
  2025-05-27: Fixed marker cleanup issue where old markers weren't removed from map
  2025-05-27: Added marker highlighting when hovering over listing items
-->
<template>
  <div class="google-map fill-height" ref="googleMap" />
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";
import FormattersMixin from "@/mixins/formatters-mixin";
import { MarkerClusterer, GridAlgorithm } from "@googlemaps/markerclusterer";
import { AMENITY_ICONS } from "@/consts/AMENITIES";
import { mapState } from "vuex";
export default {
  name: "google-map",
  props: {
    markersData: Array,
    mapOptions: Object,
    zoomToCity: String, // City name to zoom to
  },
  mixins: [FormattersMixin],
  data: () => ({
    google: null,
    map: null,
    markers: [],
    clusterer: null,
    info: null,
    geocoder: null,
    AMENITY_ICONS,
    mapCentered: false,
  }),

  async mounted() {
    new Loader({
      apiKey: "AIzaSyCzvYaJgsS0jhNhEjzREy_G9Quie3zc7A4",
      libraries: ["geometry", "geocoding"],
    })
      .load()
      .then((google) => {
        this.google = google;
        this.geocoder = new google.maps.Geocoder();
        this.initializeMap();
        console.log("map loaded successfully");
      });
  },

  watch: {
    markersData() {
      this.updateMarkers(!this.hasNearby);
    },
    hoveredListingId(newId, oldId) {
      this.highlightMarker(newId, oldId);
    },
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
      hoveredListingId: (state) => state.listingsModule.hoveredListingId,
    }),
    hasNearby() {
      return this.$route.query.nearby;
    },
    hasFilters() {
      return (
        this.$route.query.city ||
        this.$route.query.region ||
        this.$route.query.amenities ||
        this.$route.query.check_in ||
        this.$route.query.adults > 1
      );
    },
  },

  methods: {
    getIconPaths() {
      return {
        markerIcon:
          "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        priceIcon:
          "M -30 0 h 60 a 14 14 0 0 1 14 14 v 0 a 14 14 0 0 1 -14 14 h -60 a 14 14 0 0 1 -14 -14 v 0 a 14 14 0 0 1 14 -14 z",
      };
    },
    createMarkerIcon(markerData, options = {}) {
      const { markerIcon, priceIcon } = this.getIconPaths();
      const {
        fillColor = "#FFFFFF",
        strokeColor = "#000000",
        strokeWeight = 1,
        scale = markerData.price ? 0.6 : 1.4,
      } = options;

      return {
        path: markerData.price ? priceIcon : markerIcon,
        fillColor,
        fillOpacity: 1,
        strokeColor,
        strokeWeight,
        scale,
        anchor: new this.google.maps.Point(0, 14),
        labelOrigin: new this.google.maps.Point(0, 14),
      };
    },
    initializeMap() {
      this.markers = [];

      const mapContainer = this.$refs.googleMap;
      this.map = new this.google.maps.Map(mapContainer, this.mapOptions);
      this.info = new this.google.maps.InfoWindow();

      this.updateMarkers(true);

      const searchNearyby = () => {
        const bounds = this.map.getBounds();

        const center = bounds.getCenter();
        const ne = bounds.getNorthEast();
        const rad = this.google.maps.geometry.spherical.computeDistanceBetween(
          center,
          ne
        );
        this.$emit("search-nearby", {
          lng: center.lng(),
          lat: center.lat(),
          rad: rad / 1300,
        });
      };
      mapContainer.addEventListener("click", () => {
        !this.hasNearby && searchNearyby();
      });
      this.map.addListener("dragend", () => searchNearyby());
      this.map.addListener(
        "zoom_changed",
        () => this.hasNearby && searchNearyby()
      );
    },
    zoomToBiggestCluster(clusters) {
      if (!clusters.length) return; // no markers?

      const largest = clusters.reduce((best, cur) =>
        cur.count > best.count ? cur : best
      );

      // Zoom/pan so that every marker in that cluster is visible
      this.map.fitBounds(largest.bounds); // bounds is a LatLngBounds
    },

    updateMarkers(fitBounds = false) {
      // Clear existing markers using clusterer if available
      if (this.clusterer) {
        this.clusterer.clearMarkers();
      }
      this.markers.forEach((marker) => {
        marker.setMap(null);
      });
      const bounds = new this.google.maps.LatLngBounds();
      this.markers = [];
      this.markersData.forEach((m) => {
        const marker = new this.google.maps.Marker({
          id: m.id,
          position: m.position,
          marker: m,
          map: this.map,
          icon: this.createMarkerIcon(m),
          label: m.price
            ? {
                text: `${this.toDisplayCurrency(
                  m.price,
                  this.settings,
                  m.currency
                )}`,
                color: "#000000",
                fontSize: "13px",
                fontWeight: "500",
              }
            : "",
        });
        marker.addListener("click", () => {
          this.info.setContent(m.info);
          this.info.open({ anchor: marker, map: this.map });
        });
        bounds.extend(m.position);
        this.markers.push(marker);
      });
      if (fitBounds && this.markers.length) this.map.fitBounds(bounds);
      this.clusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        algorithm: new GridAlgorithm({ maxZoom: 10 }), // Cluster only when zoomed out (levels 0-10)
        renderer: {
          render: ({ count, position }) => {
            const marker = new this.google.maps.Marker({
              position,
              icon: {
                path: this.google.maps.SymbolPath.CIRCLE,
                fillColor: "#FFFFFF",
                fillOpacity: 1,
                strokeColor: "#000000",
                strokeWeight: 1,
                scale: 15,
                labelOrigin: new this.google.maps.Point(0, 0),
              },
              label: {
                text: String(count),
                color: "#000000",
                fontSize: "13px",
                fontWeight: "500",
              },
            });
            return marker;
          },
        },
      });
      if (this.markers.length === 1 && this.$route.path.includes("listing/")) {
        this.map?.setZoom(15);
      } else if (fitBounds && !this.hasFilters) {
        this.google.maps.event.addListenerOnce(this.map, "idle", () => {
          this.zoomToBiggestCity();
        });
      }
    },
    highlightMarker(hoveredId, previousId) {
      if (!this.markers.length) return;

      // Reset previous highlighted marker
      if (previousId) {
        const previousMarker = this.markers.find((m) => m.id === previousId);
        if (previousMarker) {
          this.resetMarkerStyle(previousMarker);
        }
      }

      // Highlight new marker
      if (hoveredId) {
        const currentMarker = this.markers.find((m) => m.id === hoveredId);
        if (currentMarker) {
          this.setHighlightedMarkerStyle(currentMarker);
        }
      }
    },
    resetMarkerStyle(marker) {
      const markerData = marker.marker;
      marker.setIcon(this.createMarkerIcon(markerData));
    },
    setHighlightedMarkerStyle(marker) {
      const markerData = marker.marker;
      marker.setIcon(
        this.createMarkerIcon(markerData, {
          fillColor: "#1976D2", // Primary blue color for highlight
          strokeColor: "#0D47A1", // Darker blue for stroke
          strokeWeight: 2,
          scale: markerData.price ? 0.8 : 1.6, // Slightly larger when highlighted
        })
      );
    },
    async zoomToBiggestCity() {
      const biggestCity = this.settings.cities[0];
      if (!biggestCity) return;
      const result = await this.geocoder.geocode({ address: biggestCity });
      const location = result?.results?.[0]?.geometry?.location;
      if (!location) return;
      this.map.setCenter(location);
      this.map.setZoom(12);
    },
  },
};
</script>

<style lang="scss">
.google-map {
  position: relative;
  &-info {
    width: 220px;
    img {
      width: 220px;
      height: 170px;
      object-fit: cover;
    }
    &-content {
      padding: 15px;
    }
  }
  &-amenities {
    &-item {
      color: map-get($blue-grey, "lighten-1");
      svg {
        width: auto;
        height: 14px;
      }
    }
  }
  &-favorite,
  &-close {
    position: absolute;
    display: inline-block;
    width: 20px;
    height: 20px;
    top: 16px;
    left: 16px;
    color: #ffffff;
    line-height: 0;
  }
  &-close {
    font-size: 24px;
    left: auto;
    right: 16px;
  }
}
.gm-style {
  &-iw-c {
    padding: 0 !important;
  }
  &-iw-d {
    overflow: auto !important;
  }
}
</style>
